import { useQueryClient } from "@tanstack/react-query";
import { CalendarDays, CalendarRange, ChartColumn, Footprints, Link2, LogOut, Monitor, Moon, Search, Smartphone, Sun, Tv } from "lucide-react";
import { Suspense } from "react";
import { NavLink, Outlet, useNavigate } from "react-router";
import type { MeDTO } from "../../shared/dto.js";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/cn";
import { shortDate } from "@/lib/format";
import { switchIdentity } from "@/lib/identity";
import { stopPreview } from "@/lib/preview";
import { useTheme, type Theme } from "@/lib/theme";

export function AppShell({ me, updateReady }: { me: MeDTO; updateReady: boolean }) {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const owner = me.role === "owner" && !me.preview;

  async function signOut() {
    await api.post("/api/auth/logout");
    await switchIdentity(queryClient);
  }

  async function leave() {
    await api.shares.leave();
    await switchIdentity(queryClient);
    navigate("/", { replace: true });
  }

  async function exitPreview() {
    stopPreview();
    await switchIdentity(queryClient);
    navigate("/shares");
  }

  return (
    <div className="min-h-screen">
      {me.role === "viewer" && me.share && <ShareBanner share={me.share} zone={me.zone} onLeave={() => void leave()} />}
      {me.preview && me.share && (
        <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-primary px-4 py-1.5 text-center text-xs text-primary-fg">
          <span>
            Previewing the link “{me.share.label}”: this is exactly what it shows{me.share.lastDays !== null ? `, the last ${me.share.lastDays} days` : ""}.
          </span>
          <button type="button" className="font-semibold underline" onClick={() => void exitPreview()}>
            Exit preview
          </button>
        </div>
      )}
      {updateReady && (
        <div className="bg-primary px-4 py-1.5 text-center text-xs text-primary-fg">
          NS Journal was updated.{" "}
          <button type="button" className="font-semibold underline" onClick={() => window.location.reload()}>
            Reload
          </button>
        </div>
      )}
      <nav className="sticky top-0 z-20 border-b border-line bg-bg/90 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-[100rem] items-center gap-1 px-3 sm:gap-2 sm:px-4">
          <span className="mr-2 flex items-center gap-2 font-semibold">
            <img src="/favicon.svg" alt="" className="size-6" /> <span className="hidden sm:inline">NS Journal</span>
          </span>
          <div className="flex min-w-0 items-center gap-0.5 overflow-x-auto [scrollbar-width:none] sm:gap-1 [&::-webkit-scrollbar]:hidden">
            <NavItem to="/day" icon={<CalendarDays />} label="Day" />
            <NavItem to="/week" icon={<CalendarRange />} label="Week" />
            <NavItem to="/stats" icon={<ChartColumn />} label="Stats" />
            {(me.share?.journal ?? true) && <NavItem to="/runs" icon={<Footprints />} label="Runs" />}
            {owner && <NavItem to="/search" icon={<Search />} label="Search" />}
            {owner && <NavItem to="/shares" icon={<Link2 />} label="Share" title="Read-only links for a doctor, family or a TV" />}
            {owner && <NavItem to="/devices" icon={<Smartphone />} label="Phones" title="Phones that send voice notes" />}
            <NavItem to="/now" icon={<Tv />} label="Now" title="Full-screen view for a TV or a second monitor" />
          </div>
          <div className="ml-auto flex items-center gap-1">
            <ThemeButton />
            {owner && (
              <Button variant="ghost" size="sm" onClick={signOut}>
                <LogOut /> <span className="hidden sm:inline">Sign out</span>
              </Button>
            )}
          </div>
        </div>
      </nav>
      <ZoneNote zone={me.zone} />
      <main className="mx-auto max-w-[100rem] px-3 py-4 sm:px-4 sm:py-5">
        <Suspense fallback={<div className="grid h-64 place-items-center text-sm text-muted">Loading…</div>}>
          <Outlet />
        </Suspense>
      </main>
    </div>
  );
}

function NavItem({ to, icon, label, title }: { to: string; icon: React.ReactNode; label: string; title?: string }) {
  return (
    <NavLink
      to={to}
      title={title ?? label}
      aria-label={label}
      className={({ isActive }) =>
        cn(
          "flex shrink-0 items-center gap-1.5 rounded-lg px-2.5 py-1.5 text-sm lg:px-3 [&_svg]:size-4",
          isActive ? "bg-primary-soft font-medium" : "text-muted hover:bg-surface-muted",
        )
      }
    >
      {icon}
      {/* Icons only on small screens; the nav has seven items. */}
      <span className="hidden md:inline">{label}</span>
    </NavLink>
  );
}

const THEMES: { theme: Theme; label: string; icon: React.ReactNode }[] = [
  { theme: "system", label: "Theme: system", icon: <Monitor /> },
  { theme: "light", label: "Theme: light", icon: <Sun /> },
  { theme: "dark", label: "Theme: dark", icon: <Moon /> },
];

function ThemeButton() {
  const [theme, setTheme] = useTheme();
  const index = THEMES.findIndex((t) => t.theme === theme);
  const current = THEMES[index]!;
  const next = THEMES[(index + 1) % THEMES.length]!;
  return (
    <Button variant="ghost" size="icon" aria-label={`${current.label}. Switch to ${next.theme}.`} title={current.label} onClick={() => setTheme(next.theme)}>
      {current.icon}
    </Button>
  );
}

function ShareBanner({ share, zone, onLeave }: { share: NonNullable<MeDTO["share"]>; zone: string; onLeave: () => void }) {
  const details = [
    "read-only",
    share.lastDays !== null && `the last ${share.lastDays} days`,
    share.expiresAt !== null && `until ${shortDate(share.expiresAt, zone)}`,
  ].filter(Boolean);
  return (
    <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 bg-primary-soft px-4 py-1.5 text-center text-xs">
      <span>Shared by Vivien · {details.join(" · ")}</span>
      <button type="button" className="underline" onClick={onLeave} title="Forget this link on this device">
        Leave
      </button>
    </div>
  );
}

/** Days and times follow the owner's zone; say so to a viewer whose browser is elsewhere. */
function ZoneNote({ zone }: { zone: string }) {
  let local: string | undefined;
  try {
    local = new Intl.DateTimeFormat().resolvedOptions().timeZone;
  } catch {
    local = undefined;
  }
  if (!local || local === zone) return null;
  const city = zone.split("/").pop()?.replace(/_/g, " ") ?? zone;
  return <div className="px-4 pt-2 text-center text-xs text-muted">Days and times are in {city} time ({zone}).</div>;
}
